import React, { useState, useEffect, useCallback, useMemo } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector, useDispatch } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import swal from "sweetalert";
import AddTwilioGatewayModal from "./addTwilioGatewayModal";
import ThemeButton from "../../components/CustomComponents/Buttons/ThemeButton/ThemeButton";
import AddButton from "../../components/CustomComponents/Buttons/AddButton/AddButton";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import ModernGridToolbar from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import TableActionsMenu, {
  TableActionsItem,
} from "../../components/CustomComponents/TableActionsMenu/TableActionsMenu";
import { setVisibleColumns, setFilterField } from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import {
  applyCatalogTotalFromFetch,
  applyCatalogTotalFromMutation,
} from "../../utils/catalogTotalRecords";



const COLUMN_ORDER = ["credentials", "accountSid", "sms", "status", "updated"];

const normalizeGateway = (row) => ({
  id: row.id ?? row.ID,
  accountSid: row.accountSid ?? row.AccountSid ?? "",
  apiKeySid: row.apiKeySid ?? row.ApiKeySid ?? "",
  apiKeySecretMasked: row.apiKeySecretMasked ?? row.ApiKeySecretMasked ?? "",
  hasApiKeySecret: row.hasApiKeySecret ?? row.HasApiKeySecret ?? false,
  messagingServiceSid: row.messagingServiceSid ?? row.MessagingServiceSid ?? "",
  smsSender: row.smsSender ?? row.SmsSender ?? "",
  whatsAppSender: row.whatsAppSender ?? row.WhatsAppSender ?? "",
  useServeIQTwilio: row.useServeIQTwilio ?? row.UseServeIQTwilio ?? true,
  credentialSource: row.credentialSource ?? row.CredentialSource ?? "ServeIQ",
  platformKeysConfigured: row.platformKeysConfigured ?? row.PlatformKeysConfigured ?? false,
  smsEnabled: row.smsEnabled ?? row.SmsEnabled ?? false,
  whatsAppEnabled: row.whatsAppEnabled ?? row.WhatsAppEnabled ?? false,
  status: row.status ?? row.Status ?? false,
  enteryDateTime: row.enteryDateTime ?? row.EnteryDateTime ?? "",
  updatedDateTime: row.updatedDateTime ?? row.UpdatedDateTime ?? "",
  hasAuthToken: row.hasAuthToken ?? row.HasAuthToken ?? false,
  authTokenMasked: row.authTokenMasked ?? row.AuthTokenMasked ?? "",
});

const formatUpdatedAt = (dateVal) => {
  if (!dateVal) return "—";
  try {
    const d = new Date(dateVal);
    if (Number.isNaN(d.getTime())) return String(dateVal);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(dateVal);
  }
};

const formatYearMonth = (yearMonth) => {
  const value = Number(yearMonth);
  if (!value || value < 200001) return String(yearMonth ?? "—");
  const year = Math.floor(value / 100);
  const month = value % 100;
  const d = new Date(year, month - 1, 1);
  if (Number.isNaN(d.getTime())) return String(yearMonth);
  return d.toLocaleString(undefined, { year: "numeric", month: "long" });
};

const normalizeUsageHistoryRow = (row) => ({
  usageID: row.usageID ?? row.UsageID,
  compID: row.compID ?? row.CompID,
  yearMonth: row.yearMonth ?? row.YearMonth,
  smsSegmentsUsed: row.smsSegmentsUsed ?? row.SmsSegmentsUsed ?? 0,
  whatsAppSegmentsUsed: row.whatsAppSegmentsUsed ?? row.WhatsAppSegmentsUsed ?? 0,
  smsLimit: row.smsLimit ?? row.SmsLimit ?? 0,
  whatsAppLimit: row.whatsAppLimit ?? row.WhatsAppLimit ?? 0,
  updatedDateTime: row.updatedDateTime ?? row.UpdatedDateTime ?? "",
  totalCount: row.totalCount ?? row.TotalCount ?? 0,
});

const ManageTwilioGateway = () => {
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const dispatch = useDispatch();
  const pageKey = "twilioGateway";
  const defaultVisibleColumns = {
    credentials: true,
    accountSid: true,
    sms: true,
    status: true,
    updated: true,
  };
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];

  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;

  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [catalogTotalRecords, setCatalogTotalRecords] = useState(null);
  const [twilioGateways, setTwilioGateways] = useState([]);
  const [search, setSearch] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [gatewayToEdit, setGatewayToEdit] = useState(null);
  const [platformKeysConfigured, setPlatformKeysConfigured] = useState(false);
  const [messagingQuota, setMessagingQuota] = useState(null);
  const [usageHistory, setUsageHistory] = useState([]);
  const [usageHistoryLoading, setUsageHistoryLoading] = useState(false);
  const [usageHistoryPageNumber, setUsageHistoryPageNumber] = useState(1);
  const [usageHistoryPageSize, setUsageHistoryPageSize] = useState({ value: "20", label: "20" });
  const [usageHistoryTotalRecords, setUsageHistoryTotalRecords] = useState(0);

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const usageHistoryTotalPages = Math.max(
    1,
    Math.ceil(usageHistoryTotalRecords / Number(usageHistoryPageSize.value || 20))
  );

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const fetchTwilioGateways = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_TWILIO_GATEWAY, {
        params: { CompID },
      });
      const rows = (response.data.data || []).map(normalizeGateway);
      setTwilioGateways(rows);
      applyCatalogTotalFromFetch(setCatalogTotalRecords, rows.length);
    } catch (err) {
      setTwilioGateways([]);
      showErrorToast("Failed to load Twilio settings");
    } finally {
      setLoading(false);
      setInitialLoadComplete(true);
    }
  }, [CompID]);

  const fetchPlatformStatus = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_TWILIO_PLATFORM_STATUS);
      setPlatformKeysConfigured(response.data?.data?.platformKeysConfigured === true);
    } catch {
      setPlatformKeysConfigured(false);
    }
  }, []);

  const fetchMessagingQuota = useCallback(async () => {
    if (!CompID) return;
    try {
      const response = await axiosInstance.get(APIs.BO_GET_MESSAGING_QUOTA, {
        params: { CompID },
      });
      setMessagingQuota(response.data?.data || null);
    } catch {
      setMessagingQuota(null);
    }
  }, [CompID]);

  const fetchUsageHistory = useCallback(async () => {
    if (!CompID) return;
    try {
      setUsageHistoryLoading(true);
      const response = await axiosInstance.get(APIs.BO_GET_MESSAGING_USAGE_HISTORY, {
        params: {
          CompID,
          PageNumber: usageHistoryPageNumber,
          PageSize: Number(usageHistoryPageSize.value),
        },
      });
      const rows = (response.data?.data || []).map(normalizeUsageHistoryRow);
      setUsageHistory(rows);
      setUsageHistoryTotalRecords(
        response.data?.totalRecords ?? response.data?.TotalRecords ?? rows[0]?.totalCount ?? 0
      );
    } catch {
      setUsageHistory([]);
      setUsageHistoryTotalRecords(0);
      showErrorToast("Failed to load messaging usage history");
    } finally {
      setUsageHistoryLoading(false);
    }
  }, [CompID, usageHistoryPageNumber, usageHistoryPageSize?.value]);

  useEffect(() => {
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
      dispatch(
        setVisibleColumns({
          pageKey,
          columns: mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns),
        })
      );
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey]);

  useEffect(() => {
    fetchTwilioGateways();
    fetchPlatformStatus();
    fetchMessagingQuota();
  }, [fetchTwilioGateways, fetchPlatformStatus, fetchMessagingQuota]);

  useEffect(() => {
    fetchUsageHistory();
  }, [fetchUsageHistory]);

  useEffect(() => {
    if (usageHistoryPageNumber > usageHistoryTotalPages) {
      setUsageHistoryPageNumber(usageHistoryTotalPages);
    }
  }, [usageHistoryPageNumber, usageHistoryTotalPages]);

  const handleUsageHistoryPageSizeChange = useCallback((value) => {
    setUsageHistoryPageSize(value);
    setUsageHistoryPageNumber(1);
  }, []);

  useResetPageOnFilterChange(setPageNumber, [search, pageSize?.value]);

  const filteredGateways = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return twilioGateways;
    return twilioGateways.filter((g) => {
      const haystack = [
        g.accountSid,
        g.messagingServiceSid,
        g.smsSender,
        g.useServeIQTwilio ? "serveiq" : "own account",
        g.smsEnabled ? "enabled" : "off",
        g.status ? "active" : "inactive",
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [twilioGateways, search]);

  const totalRecords = filteredGateways.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / Number(pageSize.value)));
  const pagedGateways = useMemo(() => {
    const size = Number(pageSize.value);
    const start = (pageNumber - 1) * size;
    return filteredGateways.slice(start, start + size);
  }, [filteredGateways, pageNumber, pageSize]);

  useEffect(() => {
    if (pageNumber > totalPages) setPageNumber(totalPages);
  }, [pageNumber, totalPages]);

  const handleDeleteGateway = async (gateway) => {
    const confirmDelete = await swal({
      title: "Are you sure?",
      text: "Delete Twilio settings for this restaurant?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) return;

    try {
      setLoading(true);
      const response = await axiosInstance.delete(APIs.DELETE_TWILIO_GATEWAY, {
        params: {
          ID: gateway.id,
          CompID,
        },
      });
      if (response.data.status) {
        setTwilioGateways([]);
        applyCatalogTotalFromMutation(setCatalogTotalRecords, 0);
        showSuccessToast("Twilio settings deleted successfully");
      } else {
        showErrorToast(response.data?.message || "Failed to delete Twilio settings");
      }
    } catch (err) {
      showErrorToast("Failed to delete Twilio settings");
    } finally {
      setLoading(false);
    }
  };

  const handleEditGateway = (gateway) => {
    setGatewayToEdit(gateway);
    setIsModalOpen(true);
  };

  const handleAddClick = () => {
    setGatewayToEdit(null);
    setIsModalOpen(true);
  };

  const hasFiltersApplied = () => search.trim() !== "";
  const clearFilters = () => setSearch("");

  const showEmptyCatalogState =
    initialLoadComplete &&
    !loading &&
    !hasFiltersApplied() &&
    catalogTotalRecords === 0;

  const showGridToolbar = initialLoadComplete && !showEmptyCatalogState;

  const getEmptyStateDescription = () => {
    if (search.trim()) {
      return `No Twilio settings match "${search.trim()}". Try different keywords or clear filters.`;
    }
    return "There are no Twilio settings to show. Add settings to enable SMS messaging.";
  };

  const stickyHeaderStyle = {
    position: "sticky",
    top: 0,
    zIndex: 10000,
    backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
    color: isDarkMode ? "#ffffff" : "#000000",
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Twilio Settings</li>
          </ol>
          <h1 className="page-header mb-0">Twilio Settings</h1>
          <p
            className="mb-0 mt-1 small"
            style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}
          >
            One Twilio configuration per restaurant. Used for SMS messaging.
          </p>
        </div>
        <div className="ms-auto">
          {twilioGateways.length === 0 && initialLoadComplete && (
            <ThemeButton icon="fa fa-plus" onClick={handleAddClick}>
              Add Twilio Settings
            </ThemeButton>
          )}
        </div>
      </div>

      {messagingQuota && (
        <div
          className="mb-3 p-3"
          style={{
            borderRadius: "8px",
            border: isDarkMode ? "1px solid #404040" : "1px solid #e9ecef",
            background: isDarkMode ? "#2d3436" : "#f8f9fa",
          }}
        >
          <div className="fw-semibold mb-2">Messaging balance</div>
          <div className="small mb-1" style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
            SMS wallet (open packs — no monthly reset)
          </div>
          Used {messagingQuota.smsSegmentsUsed ?? messagingQuota.SmsSegmentsUsed ?? 0}
          {" / Total "}
          {messagingQuota.smsLimit ?? messagingQuota.SmsLimit ?? 0}
          <span className="ms-2 small" style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
            ({messagingQuota.smsRemaining ?? messagingQuota.SmsRemaining ?? 0} left)
          </span>
        </div>
      )}

      <div
        className="mb-3"
        style={{
          borderRadius: "8px",
          border: isDarkMode ? "1px solid #404040" : "1px solid #e9ecef",
          background: isDarkMode ? "#1e2224" : "#ffffff",
          overflow: "hidden",
        }}
      >
        <div
          className="px-3 py-2 fw-semibold"
          style={{
            borderBottom: isDarkMode ? "1px solid #404040" : "1px solid #e9ecef",
            background: isDarkMode ? "#2d3436" : "#f8f9fa",
          }}
        >
          Messaging usage history
        </div>
        <GridSectionLoader loading={usageHistoryLoading} minHeight="180px">
          {usageHistory.length === 0 && !usageHistoryLoading ? (
            <div className="p-3 small" style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
              No usage history yet. Entries appear after outbound SMS segments are sent.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr style={stickyHeaderStyle}>
                    <th>Month</th>
                    <th>SMS used</th>
                    <th>SMS wallet total</th>
                    <th>Last updated</th>
                  </tr>
                </thead>
                <tbody>
                  {usageHistory.map((row) => (
                    <tr key={row.usageID ?? row.yearMonth}>
                      <td className="align-middle">{formatYearMonth(row.yearMonth)}</td>
                      <td className="align-middle">{row.smsSegmentsUsed}</td>
                      <td className="align-middle">{row.smsLimit}</td>
                      <td className="align-middle">{formatUpdatedAt(row.updatedDateTime)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {(usageHistoryTotalRecords > 0 || usageHistory.length > 0) && (
            <div className="px-2 pb-2">
              <ModernGridPagerFooter
                pageSize={usageHistoryPageSize}
                onPageSizeChange={handleUsageHistoryPageSizeChange}
                totalRecords={usageHistoryTotalRecords}
                pageNumber={usageHistoryPageNumber}
                totalPages={usageHistoryTotalPages}
                setPageNumber={setUsageHistoryPageNumber}
              />
            </div>
          )}
        </GridSectionLoader>
      </div>

      <AddTwilioGatewayModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        compID={CompID}
        gatewayToEdit={gatewayToEdit}
        setTwilioGateways={(rows) =>
          setTwilioGateways((Array.isArray(rows) ? rows : [rows]).map(normalizeGateway))
        }
        platformKeysConfigured={platformKeysConfigured}
        onTwilioGatewaySubmit={() => {
          fetchTwilioGateways();
          fetchPlatformStatus();
        }}
      />

      <div className="mb-3 mt-2">
        {!initialLoadComplete && <GridSectionLoader loading minHeight="400px" />}

        {showEmptyCatalogState && (
          <AddButton onClick={handleAddClick} title="Add Twilio Settings" />
        )}

        {showGridToolbar && (
          <ModernGridToolbar
            isDarkMode={isDarkMode}
            title="Twilio Settings"
            totalCount={totalRecords}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by account, SID, status..."
            bodyFlush
            headerControls={
              <ColumnSelection
                visibleColumns={visibleColumns}
                columnOrder={COLUMN_ORDER}
                setVisibleColumns={(cols) =>
                  dispatch(setVisibleColumns({ pageKey, columns: cols }))
                }
              />
            }
          >
            <div className="px-3 pb-3">
              <GridSectionLoader loading={loading} minHeight="280px">
                {!loading && pagedGateways.length === 0 && (
                  <GridEmptyState
                    isDarkMode={isDarkMode}
                    themeColor={themeColor}
                    title="No Twilio settings found"
                    description={getEmptyStateDescription()}
                    onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
                    clearFiltersLabel="Clear all filters"
                  />
                )}

                {!loading && pagedGateways.length > 0 && (
                  <div
                    className="table-responsive table-container"
                    style={{
                      minHeight: "200px",
                      maxHeight: "450px",
                      overflowY: "auto",
                      overflowX: "auto",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <table className="table table-hover">
                      <thead className="bg-light sticky-header" style={stickyHeaderStyle}>
                        <tr>
                          {visibleColumns.credentials && (
                            <th className="pt-2 pb-2">Credentials</th>
                          )}
                          {visibleColumns.accountSid && (
                            <th className="pt-2 pb-2">Account / Subaccount</th>
                          )}
                          {visibleColumns.sms && <th className="pt-2 pb-2">SMS</th>}
                          {/* WhatsApp column hidden for now — match modal
                          {visibleColumns.whatsApp && (
                            <th className="pt-2 pb-2">WhatsApp</th>
                          )}
                          */}
                          {visibleColumns.status && <th className="pt-2 pb-2">Status</th>}
                          {visibleColumns.updated && <th className="pt-2 pb-2">Updated</th>}
                          <th className="pt-2 pb-2 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedGateways.map((gateway) => (
                          <tr key={gateway.id}>
                            {visibleColumns.credentials && (
                              <td className="align-middle">
                                <span
                                  className={`badge ${
                                    gateway.useServeIQTwilio ? "bg-primary" : "bg-info"
                                  }`}
                                >
                                  {gateway.useServeIQTwilio ? "ServeIQ" : "Own account"}
                                </span>
                              </td>
                            )}
                            {visibleColumns.accountSid && (
                              <td
                                className="align-middle"
                                onClick={() => handleEditGateway(gateway)}
                                style={{ cursor: "pointer" }}
                              >
                                {gateway.accountSid || "—"}
                              </td>
                            )}
                            {visibleColumns.sms && (
                              <td className="align-middle">
                                <span
                                  className={`badge ${
                                    gateway.smsEnabled ? "bg-success" : "bg-secondary"
                                  }`}
                                >
                                  {gateway.smsEnabled ? "Enabled" : "Off"}
                                </span>
                                {gateway.smsEnabled && gateway.messagingServiceSid && (
                                  <div className="small text-muted mt-1">
                                    {gateway.messagingServiceSid}
                                  </div>
                                )}
                                {gateway.smsEnabled &&
                                  gateway.smsSender &&
                                  !gateway.messagingServiceSid && (
                                    <div className="small text-muted mt-1">
                                      {gateway.smsSender}
                                    </div>
                                  )}
                              </td>
                            )}
                            {visibleColumns.status && (
                              <td className="align-middle">
                                <span
                                  className={`badge ${
                                    gateway.status ? "bg-success" : "bg-secondary"
                                  }`}
                                >
                                  {gateway.status ? "Active" : "Inactive"}
                                </span>
                              </td>
                            )}
                            {visibleColumns.updated && (
                              <td className="align-middle">
                                {formatUpdatedAt(gateway.updatedDateTime)}
                              </td>
                            )}
                            <td className="align-middle text-center">
                              <TableActionsMenu menuEnd>
                                <TableActionsItem
                                  icon="fa fa-edit"
                                  onClick={() => handleEditGateway(gateway)}
                                >
                                  Edit
                                </TableActionsItem>
                                <TableActionsItem
                                  danger
                                  icon="fa fa-trash"
                                  onClick={() => handleDeleteGateway(gateway)}
                                >
                                  Delete
                                </TableActionsItem>
                              </TableActionsMenu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <ModernGridPagerFooter
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  totalRecords={totalRecords}
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                  setPageNumber={setPageNumber}
                />
              </GridSectionLoader>
            </div>
          </ModernGridToolbar>
        )}
      </div>
    </div>
  );
};

export default ManageTwilioGateway;
